/**
 * 图形写入协调器（提速计划 阶段 1，2026-09-15）。**先改画面，再存。**
 *
 * 之前每个增删改都等服务端响应才写缓存，于是：
 * - 新画的线在最后一击后消失 300–800ms（预览已清，响应未到）；
 * - 拖拽松手先弹回原位，响应到了再跳到新位置；
 * - 改色、删除都要等一整个往返（生产实测改色 778ms）。
 *
 * 现在每个动作**同步**写进画布缓存（下一帧就画出来），请求在后台发。为此要解决三件事，
 * 都在这里，不散落到页面里：
 *
 * 1. **临时 id**。新线在服务端返回前没有 id，用负数占位。响应回来前对它做的改色、拖拽、
 *    删除进它自己的队列，拿到真实 id 后按顺序发出。
 * 2. **同一条线串行 + 版本号**。旧请求的响应不能覆盖更新的本地改动：发请求时记下本地
 *    版本，响应回来若版本已变，只采纳服务端独有的信息（id），不覆盖本地字段。
 * 3. **失败要看得见**。新建失败 → 撤掉那条线；改/删失败 → 重取画布恢复服务端真相。两者都
 *    通过 `onError` 告诉用户，不静默。
 *
 * 样式改动 300ms 内合并成一次请求（连续点几个颜色只存最后一个）。页面隐藏时立即冲刷。
 */
import type { QueryClient } from '@tanstack/react-query';
import type { Anchor, CanvasResponse, DrawObject } from './types';

export interface CreateResult { object: DrawObject; created: boolean; notice: string | null }

export interface WriterTransport {
  create: (body: Record<string, unknown>) => Promise<CreateResult>;
  patch: (id: number, patch: Record<string, unknown>) => Promise<DrawObject>;
  remove: (id: number) => Promise<void>;
}

export interface WriterHooks {
  /** 写失败。`message` 是给用户看的一句话。 */
  onError: (message: string) => void;
  /** 新建成功且服务端带了提示（如分钟线已入自选股池）。 */
  onNotice: (message: string) => void;
  /** 临时 id 换成了真实 id（页面据此把选中态跟过去）。 */
  onIdResolved: (tempId: number, realId: number) => void;
}

interface Entry {
  clientId: string;
  symbol: string;
  /** 当前在缓存里用的 id（新建未返回前是负数）。 */
  id: number;
  serverId: number | null;
  version: number;
  chain: Promise<void>;
  deleted: boolean;
  failed: boolean;
  styleTimer: ReturnType<typeof setTimeout> | null;
  pendingStyle: DrawObject['style'] | null;
}

export const STYLE_COALESCE_MS = 300;

let tempSeq = 0;
function nextTempId(): number { tempSeq += 1; return -tempSeq; }

export class ObjectWriter {
  private byClient = new Map<string, Entry>();
  private byId = new Map<number, Entry>();

  constructor(
    private readonly qc: QueryClient,
    private readonly transport: WriterTransport,
    private readonly hooks: WriterHooks,
  ) {}

  // ── 缓存读写 ────────────────────────────────────────────────
  private edit(symbol: string, apply: (objs: DrawObject[]) => DrawObject[]) {
    this.qc.setQueriesData<CanvasResponse>(
      { queryKey: ['drawing-desk', 'canvas', symbol] },
      (old) => (old ? { ...old, objects: apply(old.objects) } : old),
    );
  }

  private find(symbol: string, id: number): DrawObject | null {
    for (const [, d] of this.qc.getQueriesData<CanvasResponse>({ queryKey: ['drawing-desk', 'canvas', symbol] })) {
      const o = d?.objects.find((x) => x.id === id);
      if (o) return o;
    }
    return null;
  }

  private entryFor(symbol: string, id: number): Entry | null {
    const hit = this.byId.get(id);
    if (hit) return hit.symbol === symbol ? hit : null;
    const o = this.find(symbol, id);
    if (!o || id < 0) return null;
    const e: Entry = {
      clientId: o.client_id, symbol, id, serverId: id, version: 0,
      chain: Promise.resolve(), deleted: false, failed: false, styleTimer: null, pendingStyle: null,
    };
    this.byClient.set(e.clientId, e);
    this.byId.set(id, e);
    return e;
  }

  /** 在这条线自己的队列末尾执行；前面的失败不阻塞后面（各自处理）。 */
  private enqueue(e: Entry, job: () => Promise<void>) {
    e.chain = e.chain.then(job, job);
  }

  private invalidate(symbol: string) {
    void this.qc.invalidateQueries({ queryKey: ['drawing-desk', 'canvas', symbol] });
  }

  // ── 新建 ────────────────────────────────────────────────────
  /**
   * @param optimistic 用本地已知的视图坐标拼好的对象（`anchors_k` / `anchors_view` 必填），
   *                   `id` 会被替换成临时 id。
   */
  create(symbol: string, body: Record<string, unknown>, optimistic: DrawObject): number {
    const tempId = nextTempId();
    const e: Entry = {
      clientId: optimistic.client_id, symbol, id: tempId, serverId: null, version: 1,
      chain: Promise.resolve(), deleted: false, failed: false, styleTimer: null, pendingStyle: null,
    };
    this.byClient.set(e.clientId, e);
    this.byId.set(tempId, e);
    this.edit(symbol, (objs) => [...objs.filter((o) => o.client_id !== e.clientId), { ...optimistic, id: tempId }]);

    const sentVersion = e.version;
    this.enqueue(e, async () => {
      try {
        const res = await this.transport.create(body);
        const real = res.object;
        e.serverId = real.id;
        this.byId.set(real.id, e);
        if (e.deleted) return;                      // 已被删：队列里后面那个删除会用上 serverId
        const current = this.find(symbol, e.id);
        this.edit(symbol, (objs) => {
          const rest = objs.filter((o) => o.client_id !== e.clientId);
          // 响应期间本地又改过（换色、拖动）→ 保留本地字段，只换 id
          const merged = current && e.version !== sentVersion ? { ...current, id: real.id } : real;
          return [...rest, merged];
        });
        this.byId.delete(e.id);
        const prevId = e.id;
        e.id = real.id;
        if (prevId !== real.id) this.hooks.onIdResolved(prevId, real.id);
        if (res.notice) this.hooks.onNotice(res.notice);
      } catch {
        e.failed = true;
        this.edit(symbol, (objs) => objs.filter((o) => o.client_id !== e.clientId));
        this.hooks.onError('这条线没保存上，已从图上撤回，请重画');
      }
    });
    return tempId;
  }

  // ── 改样式（合并） ──────────────────────────────────────────
  patchStyle(symbol: string, id: number, style: DrawObject['style']) {
    const e = this.entryFor(symbol, id);
    if (!e || e.deleted) return;
    e.version += 1;
    this.edit(symbol, (objs) => objs.map((o) => (o.id === e.id ? { ...o, style } : o)));
    e.pendingStyle = style;
    if (e.styleTimer) clearTimeout(e.styleTimer);
    e.styleTimer = setTimeout(() => this.flushStyle(e), STYLE_COALESCE_MS);
  }

  private flushStyle(e: Entry) {
    if (e.styleTimer) { clearTimeout(e.styleTimer); e.styleTimer = null; }
    const style = e.pendingStyle;
    if (!style) return;
    e.pendingStyle = null;
    this.send(e, { style });
  }

  // ── 改锚点（拖拽） ──────────────────────────────────────────
  patchAnchors(
    symbol: string, id: number, anchors: Anchor[],
    view: { ks: Array<number | null>; ys: Array<number | null> },
  ) {
    const e = this.entryFor(symbol, id);
    if (!e || e.deleted) return;
    e.version += 1;
    this.edit(symbol, (objs) => objs.map((o) => (o.id === e.id
      ? { ...o, anchors, anchors_k: view.ks, anchors_view: view.ys }
      : o)));
    this.send(e, { anchors });
  }

  private send(e: Entry, patch: Record<string, unknown>) {
    const sentVersion = e.version;
    this.enqueue(e, async () => {
      if (e.failed || e.deleted || e.serverId == null) return;
      try {
        const obj = await this.transport.patch(e.serverId, patch);
        // 发出之后本地又变过 → 不用这份响应覆盖（下一条请求的响应会带来最终状态）
        if (e.deleted || e.version !== sentVersion) return;
        // **合并，不整体替换**：阶段 4 起只改样式的 PATCH 只回样式字段，整体替换会把锚点抹掉、
        // 渲染层读 undefined 直接崩（浏览器实测过这个形状）。
        this.edit(e.symbol, (objs) => objs.map((o) => (o.id === e.id ? { ...o, ...obj, id: e.id } : o)));
      } catch {
        this.hooks.onError('改动没保存上，已恢复成服务器上的样子');
        this.invalidate(e.symbol);
      }
    });
  }

  // ── 删除 ────────────────────────────────────────────────────
  remove(symbol: string, id: number): Promise<boolean> {
    const e = this.entryFor(symbol, id);
    if (!e || e.deleted) return Promise.resolve(false);
    let removed = true;
    e.deleted = true;
    e.version += 1;
    if (e.styleTimer) { clearTimeout(e.styleTimer); e.styleTimer = null; }
    e.pendingStyle = null;
    this.edit(symbol, (objs) => objs.filter((o) => o.client_id !== e.clientId));
    this.enqueue(e, async () => {
      if (e.failed || e.serverId == null) return;   // 新建本就没成功：没有东西可删
      try {
        await this.transport.remove(e.serverId);
      } catch {
        removed = false;
        e.deleted = false; // 重取后允许用户再次删除，不能永久留在“已删除”状态。
        this.hooks.onError('没删掉，这条线已放回图上');
        this.invalidate(e.symbol);
      }
    });
    return e.chain.then(() => removed);
  }

  /** 只删二次确认时的快照，逐条等待，避免大量 DELETE 同时挤向 API。 */
  async removeMany(symbol: string, ids: readonly number[]): Promise<{ deleted: number; failed: number }> {
    const snapshot = [...new Set(ids)];
    let deleted = 0;
    for (const id of snapshot) if (await this.remove(symbol, id)) deleted += 1;
    // 单条失败会在批量仍在删除时触发 GET；收尾再取一次，避免旧响应把已删图形画回来。
    if (snapshot.length) await this.qc.invalidateQueries({ queryKey: ['drawing-desk', 'canvas', symbol] });
    return { deleted, failed: snapshot.length - deleted };
  }

  /** 页面隐藏 / 卸载前调用：把合并窗口里还没发出的样式立即发出。 */
  flush() {
    for (const e of this.byClient.values()) if (e.pendingStyle) this.flushStyle(e);
  }

  /** 测试用：等所有队列跑完。 */
  async settle() {
    await Promise.all([...this.byClient.values()].map((e) => e.chain));
  }
}
